import React from 'react';
import { createUseStyles } from 'react-jss';
import { Typography, TypographyProps, Icon, StsIconName } from '@packages/ui/shared';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

export type CaptionType = 'increase' | 'decrease' | 'unchanged';
type CaptionValueLayout = 'left' | 'right';

export const typeCaptionStyles: Record<
    CaptionType,
    {
        icon: StsIconName;
        color: keyof typeof colors;
        iconBgColor: keyof typeof colors;
        variant: TypographyProps['variant'];
    }
> = {
    decrease: { icon: 'chevron-down', color: 'greenOne', iconBgColor: 'greenFour', variant: 'statusBadge' },
    increase: { icon: 'chevron-up', color: 'redOne', iconBgColor: 'redFour', variant: 'statusBadge' },
    unchanged: { icon: 'minus', color: 'grayFive', iconBgColor: 'grayOne', variant: 'statusBadge' },
};

export function getCaptionType(value: number | string): CaptionType {
    const numericValue = Number(value);
    if (!value || isNaN(numericValue) || numericValue === 0) {
        return 'unchanged';
    }
    return numericValue > 0 ? 'increase' : 'decrease';
}

const DEFAULT_COLOR: keyof typeof colors = 'grayFive';
const DEFAULT_ICON: StsIconName = 'minus';
const DEFAULT_TEXT_VARIANT: TypographyProps['variant'] = 'caption';

const useStyles = createUseStyles({
    iconWrapper: ({ type }: { type?: CaptionType } = {}) => ({
        alignItems: 'center',
        borderRadius: '50%',
        display: 'flex',
        height: 16,
        justifyContent: 'center',
        width: 16,
        marginRight: 8,
        backgroundColor: colors[type ? typeCaptionStyles[type].iconBgColor : 'grayFour'],
    }),
    captionWrapper: ({ layout }: { layout?: CaptionValueLayout } = {}) => ({
        order: layout === 'right' ? 1 : 2,
    }),
    valueWrapper: ({ layout }: { layout?: CaptionValueLayout } = {}) => ({
        order: layout === 'right' ? 2 : 1,
        marginLeft: layout === 'right' ? 'auto' : 0,
        paddingRight: layout === 'right' ? 0 : 8,
    }),
});

export interface StatCardCaptionProps {
    className?: string;
    color?: keyof typeof colors;
    iconName?: StsIconName;
    layout?: CaptionValueLayout;
    showIcon?: boolean;
    text: string;
    textVariant?: TypographyProps['variant'];
    type?: CaptionType;
    value?: string;
}

export function StatCardCaption({
    className,
    color,
    iconName,
    layout,
    showIcon,
    text,
    textVariant,
    type,
    value,
}: StatCardCaptionProps) {
    const { t } = useTranslation();
    const classes = useStyles({ layout, type });

    const iconType = iconName || (type ? typeCaptionStyles[type].icon : DEFAULT_ICON);
    const colorType = color || (type ? typeCaptionStyles[type].color : DEFAULT_COLOR);
    const textVariantType = textVariant || (type ? typeCaptionStyles[type].variant : DEFAULT_TEXT_VARIANT);

    return (
        <div className={`d-flex align-items-center ${className}`} data-testid="caption-container">
            {showIcon && (
                <div className={`${classes.iconWrapper}`}>
                    <Icon name={iconType} size={14} color={colorType} data-testid="caption-icon" />
                </div>
            )}

            {value && (
                <div className={`d-flex align-items-center ${classes.valueWrapper}`} data-testid="caption-value">
                    <Typography variant={textVariantType} color={colorType}>
                        {t(value)}
                    </Typography>
                </div>
            )}

            <div className={`d-flex align-items-center ${classes.captionWrapper}`}>
                <Typography variant={textVariantType} color={colorType}>
                    {t(text)}
                </Typography>
            </div>
        </div>
    );
}
